import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../FirebaseAppContext';
import { HybridIcon } from '../components/HybridIcon';
import type { Car, Customer, Rental } from '../models';
import './Rentals.css';

function getRentalStatus(rental: Rental, today: Date) {
  const start = new Date(rental.startDate);
  const end = rental.endDate ? new Date(rental.endDate) : undefined;

  if (end && end < today) {
    return { label: 'Completed', pillClass: 'pill-danger', icon: 'checkmark-done' };
  }
  if (start > today) {
    return { label: 'Upcoming', pillClass: 'pill-accent', icon: 'calendar' };
  }
  return { label: 'Active', pillClass: 'pill-good', icon: 'flash' };
}

function getInitials(name?: string) {
  if (!name) {
    return 'NA';
  }
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function RentalRow({
  rental,
  car,
  customer,
  today,
  onPress,
}: {
  rental: Rental;
  car?: Car;
  customer?: Customer;
  today: Date;
  onPress: () => void;
}) {
  const status = getRentalStatus(rental, today);
  const total = rental.totalCost ?? rental.amountDue ?? 0;

  return (
    <button type="button" className="rentals-row lrow" onClick={onPress}>
      <div className="avatar rentals-avatar">{getInitials(customer?.name)}</div>
      <div className="rentals-row-main">
        <div className="rentals-row-heading">
          <h3 className="rentals-row-title">{customer?.name || 'Unknown customer'}</h3>
          <span className={`pill ${status.pillClass}`}>
            <HybridIcon name={status.icon} size={14} color="currentColor" />
            {status.label}
          </span>
        </div>
        <p className="rentals-row-subtitle">
          {car ? `${car.year} ${car.make} ${car.model}` : 'Vehicle pending'}
        </p>
        <div className="rentals-row-meta">
          <span>
            <HybridIcon name="calendar" size={14} color="currentColor" />
            {new Date(rental.startDate).toLocaleDateString()} to {rental.endDate ? new Date(rental.endDate).toLocaleDateString() : 'open'}
          </span>
          <span>
            <HybridIcon name="card" size={14} color="currentColor" />
            {car?.licensePlate || 'No plate'}
          </span>
        </div>
      </div>
      <div className="rentals-row-side">
        <strong>{total ? `$${total.toLocaleString()}` : 'Quote pending'}</strong>
        <HybridIcon name="chevron-forward" size={18} color="currentColor" />
      </div>
    </button>
  );
}

export default function Rentals() {
  const { state } = useAppContext();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const rentals = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return state.rentals;
    }

    return state.rentals.filter((rental) => {
      const car = state.cars.find((entry) => entry.id === rental.carId);
      const customer = state.customers.find((entry) => entry.id === rental.customerId);
      return (
        (rental.startDate || '').toLowerCase().includes(normalizedQuery) ||
        (rental.endDate || '').toLowerCase().includes(normalizedQuery) ||
        (car?.licensePlate || '').toLowerCase().includes(normalizedQuery) ||
        (car?.make || '').toLowerCase().includes(normalizedQuery) ||
        (car?.model || '').toLowerCase().includes(normalizedQuery) ||
        String(car?.year || '').includes(normalizedQuery) ||
        (customer?.name || '').toLowerCase().includes(normalizedQuery) ||
        (customer?.email || '').toLowerCase().includes(normalizedQuery) ||
        (customer?.phone || '').toLowerCase().includes(normalizedQuery) ||
        (customer?.contactInfo || '').toLowerCase().includes(normalizedQuery)
      );
    });
  }, [query, state.cars, state.customers, state.rentals]);

  const stats = useMemo(() => {
    let active = 0;
    let upcoming = 0;
    let completed = 0;

    state.rentals.forEach((rental) => {
      const status = getRentalStatus(rental, today).label;
      if (status === 'Active') {
        active += 1;
      } else if (status === 'Upcoming') {
        upcoming += 1;
      } else {
        completed += 1;
      }
    });

    return { total: state.rentals.length, active, upcoming, completed };
  }, [state.rentals, today]);

  return (
    <div className="rentals-page">
      <section className="rentals-hero card">
        <div>
          <p className="eyebrow">Rentals</p>
          <h1 className="rentals-title">Bookings, handoffs, and returns in one queue.</h1>
          <p className="rentals-subtitle">
            Keep pickup timing, customer context, and vehicle assignment visible without opening each record.
          </p>
        </div>
        <button type="button" className="btn btn-primary btn-lg" onClick={() => navigate('/rentals/new')}>
          <HybridIcon name="add-circle" size={20} color="currentColor" />
          <span>Book Rental</span>
        </button>
      </section>

      <section className="rentals-stats-grid">
        <div className="metric">
          <span>Total rentals</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="metric">
          <span>Active</span>
          <strong>{stats.active}</strong>
        </div>
        <div className="metric">
          <span>Upcoming</span>
          <strong>{stats.upcoming}</strong>
        </div>
        <div className="metric">
          <span>Completed</span>
          <strong>{stats.completed}</strong>
        </div>
      </section>

      <section className="rentals-toolbar card">
        <div className="rentals-search-container">
          <HybridIcon name="search" size={18} color="currentColor" className="rentals-search-icon" />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by plate, car, customer, or dates"
            className="rentals-search-input"
          />
          {query ? (
            <button className="rentals-clear-button" onClick={() => setQuery('')} type="button" aria-label="Clear search">
              <HybridIcon name="close-circle" size={18} color="currentColor" />
            </button>
          ) : null}
        </div>
        <span className="pill pill-accent">{rentals.length} matching</span>
      </section>

      {rentals.length === 0 ? (
        <section className="rentals-empty card">
          <div className="avatar rentals-avatar-lg">
            <HybridIcon name="calendar-outline" size={26} color="currentColor" />
          </div>
          <h2>No rentals found</h2>
          <p>Try a broader search, or book a new rental to start filling the calendar.</p>
        </section>
      ) : (
        <section className="rentals-list">
          {rentals.map((rental) => {
            const car = state.cars.find((entry) => entry.id === rental.carId);
            const customer = state.customers.find((entry) => entry.id === rental.customerId);
            return (
              <RentalRow
                key={rental.id}
                rental={rental}
                car={car}
                customer={customer}
                today={today}
                onPress={() => navigate(`/rentals/${rental.id}`)}
              />
            );
          })}
        </section>
      )}
    </div>
  );
}
